/**
 * Prism Theme — theme.js
 * Full-featured JS: cart, search, nav, slideshow, tabs, accordion, countdown,
 * before/after, scroll animations, back-to-top, cookie banner, quantity inputs
 */

'use strict';

/* ============================================================
   UTILITIES
   ============================================================ */

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);

function debounce(fn, delay = 300) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
}

/* Scroll lock — prevents scrollbar-width jump when drawers open */
const ScrollLock = {
  _count: 0,
  lock() {
    if (this._count === 0) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', sw + 'px');
      document.body.style.paddingRight = sw + 'px';
      document.body.classList.add('overflow-hidden');
    }
    this._count++;
  },
  unlock() {
    this._count = Math.max(0, this._count - 1);
    if (this._count === 0) {
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
      document.documentElement.style.removeProperty('--scrollbar-width');
    }
  }
};

function formatMoney(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  // Use shop.money_format set in theme.liquid (e.g. "${{amount}}")
  const fmt = window.theme && window.theme.moneyFormat;
  if (fmt) {
    const amount     = (n / 100).toFixed(2);
    const amountNoDec = String(Math.round(n / 100));
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDec.replace('.', ','));
  }
  // Fallback: Intl formatter
  try {
    const currency = (window.theme && window.theme.currency) ||
                     (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
                     'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n / 100);
  } catch (_) {
    return '$' + (n / 100).toFixed(2);
  }
}

/* ============================================================
   CART
   ============================================================ */

const Cart = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async add(id, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity: qty })
    });
    const data = await res.json();
    if (!res.ok) throw data; // surfaces Shopify's inventory error message
    return data;
  },
  async change(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    return res.json();
  },
  async remove(key) {
    return Cart.change(key, 0);
  },
  async updateNote(note) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note })
    });
  }
};

/* ============================================================
   CART DRAWER
   ============================================================ */

const CartDrawer = {
  drawer: null,

  init() {
    this.drawer = $('#cart-drawer');
    if (!this.drawer) return;

    // Open / close via event delegation so it survives DOM replacement
    on(document, 'click', (e) => {
      if (e.target.closest('[data-open-cart]'))  this.open();
      if (e.target.closest('[data-close-cart]')) this.close();
    });

    // Quantity changes + remove (delegation — survives section re-renders)
    on(document, 'click', async (e) => {
      const qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        const key      = qtyBtn.dataset.qtyChange;
        const change   = parseInt(qtyBtn.dataset.change);
        const itemEl   = qtyBtn.closest('.cart-item');
        const qtyEl    = itemEl && itemEl.querySelector('.cart-item__qty-value');
        const currentQty = parseInt(qtyEl && qtyEl.textContent) || 0;
        let newQty = Math.max(0, currentQty + change);

        // Client-side inventory cap
        if (change > 0 && qtyBtn.dataset.max !== undefined) {
          const maxQty = parseInt(qtyBtn.dataset.max);
          if (newQty > maxQty) newQty = maxQty;
          if (newQty === currentQty) return;
        }

        // Optimistic: update qty and total display immediately
        if (qtyEl) qtyEl.textContent = newQty;
        this._optimisticTotal(change);

        // Disable buttons on this row during request
        const rowBtns = itemEl ? itemEl.querySelectorAll('button') : [];
        rowBtns.forEach(b => { b.disabled = true; });

        await Cart.change(key, newQty);
        await this.refreshSection();
        return;
      }

      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        const itemEl = removeBtn.closest('.cart-item');
        if (itemEl) itemEl.style.opacity = '0.4';
        await Cart.remove(removeBtn.dataset.removeItem);
        await this.refreshSection();
      }
    });

    // Cart note
    on(document, 'change', debounce(async (e) => {
      const note = e.target.closest('[data-cart-note]');
      if (note) await Cart.updateNote(note.value);
    }, 500));

    on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });
  },

  open() {
    this.drawer = this.drawer || $('#cart-drawer');
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    ScrollLock.lock();
  },

  close() {
    this.drawer = this.drawer || $('#cart-drawer');
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    ScrollLock.unlock();
  },

  // Re-render the full drawer via Shopify's Section Rendering API.
  // This guarantees accurate inventory, prices, and totals from Liquid.
  async refreshSection() {
    try {
      const res  = await fetch('/?sections=cart-drawer');
      const data = await res.json();
      const html = data && data['cart-drawer'];
      if (!html) return;

      const doc       = new DOMParser().parseFromString(html, 'text/html');
      const newDrawer = doc.getElementById('cart-drawer');
      if (!newDrawer) return;

      const oldDrawer = document.getElementById('cart-drawer');
      if (!oldDrawer) return;

      // Preserve open state across replacement
      if (oldDrawer.classList.contains('is-open')) {
        newDrawer.classList.add('is-open');
        newDrawer.setAttribute('aria-hidden', 'false');
      }

      oldDrawer.replaceWith(newDrawer);
      this.drawer = document.getElementById('cart-drawer');

      // Sync count badge in the header (outside the drawer)
      const drawerCount = this.drawer && $('[data-cart-count]', this.drawer);
      if (drawerCount) {
        $$('[data-cart-count]').forEach(el => el.textContent = drawerCount.textContent);
      }
    } catch (err) {
      console.error('Cart drawer refresh failed:', err);
    }
  },

  // Optimistic subtotal nudge while API call is in-flight.
  // Reads the current total text, parses it, and adjusts by the unit price * change.
  // The section refresh will correct it precisely afterward.
  _optimisticTotal(unitChange) {
    const totalEl = this.drawer && $('[data-cart-total]', this.drawer);
    if (!totalEl) return;
    // Find unit price from the same row — not easily available; skip nudge for now.
    // The section refresh is fast enough that this is acceptable.
  }
};

/* ============================================================
   SEARCH DRAWER
   ============================================================ */

const SearchDrawer = {
  drawer: null,

  init() {
    this.drawer = $('#search-drawer');
    if (!this.drawer) return;

    $$('[data-open-search]').forEach(btn => on(btn, 'click', () => this.open()));
    $$('[data-close-search]').forEach(btn => on(btn, 'click', () => this.close()));
    on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });
  },

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    ScrollLock.lock();
    setTimeout(() => {
      const input = this.drawer.querySelector('[data-search-input]');
      if (input) input.focus();
    }, 100);
  },

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    ScrollLock.unlock();
  }
};

/* ============================================================
   HEADER — sticky + transparent + mobile nav
   ============================================================ */

const Header = {
  el: null,
  mobileNav: null,
  menuToggle: null,
  scrolled: false,

  init() {
    this.el = $('[data-header]');
    if (!this.el) return;
    this.mobileNav = $('#mobile-nav');
    this.mobileOverlay = $('#mobile-nav-overlay');
    this.menuToggle = $('[data-menu-toggle]');

    // Sticky header spacer — inject a real in-flow spacer AFTER the fixed section wrapper
    // (spacer cannot live inside the fixed section — fixed elements are removed from flow)
    // Skip when transparent: the hero section intentionally slides under the header
    if (this.el.dataset.sticky === 'true' && this.el.dataset.transparent !== 'true') {
      const sectionEl = this.el.closest('[id^="shopify-section"]') || this.el.parentElement;
      let spacer = document.getElementById('header-spacer');
      if (!spacer) {
        spacer = document.createElement('div');
        spacer.id = 'header-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        sectionEl.after(spacer);
      }
      const syncSpacer = () => { spacer.style.height = sectionEl.offsetHeight + 'px'; };
      syncSpacer();
      window.addEventListener('resize', debounce(syncSpacer, 100), { passive: true });
    }

    // Mobile toggle — all hamburger buttons (layout variants each render one)
    $$('[data-menu-toggle]').forEach(btn =>
      on(btn, 'click', () => this.toggleMobileNav())
    );

    // Close mobile nav on overlay click
    on(this.mobileOverlay, 'click', () => this.closeMobileNav());

    // Close on Escape key
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && this.mobileNav && this.mobileNav.classList.contains('is-open')) {
        this.closeMobileNav();
      }
    });

    // When search opens from inside the mobile drawer, close the nav first
    const mobileSearchBtn = $('#mobile-search-btn');
    if (mobileSearchBtn) {
      on(mobileSearchBtn, 'click', () => this.closeMobileNav());
    }

    // Mobile nav close + accordion
    this.initMobilePanels();

    // Sticky + transparent handling
    this.handleScroll();
    let scrollTicking = false;
    on(window, 'scroll', () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => { this.handleScroll(); scrollTicking = false; });
        scrollTicking = true;
      }
    }, { passive: true });

    // Mega menu hover on desktop
    $$('.nav__item--has-mega').forEach(item => {
      on(item, 'mouseenter', () => item.classList.add('is-open'));
      on(item, 'mouseleave', () => item.classList.remove('is-open'));
    });
  },

  handleScroll() {
    const scrolled = window.scrollY > 50;
    if (scrolled !== this.scrolled) {
      this.scrolled = scrolled;
      if (this.el) {
        this.el.classList.toggle('is-scrolled', scrolled);
        // When scrolled, remove the transparent class so header gets a solid bg
        if (this.el.dataset.transparent === 'true') {
          this.el.classList.toggle('site-header--transparent', !scrolled);
        }
      }
    }
  },

  toggleMobileNav() {
    if (!this.mobileNav) return;
    const open = this.mobileNav.classList.toggle('is-open');
    if (this.mobileOverlay) this.mobileOverlay.classList.toggle('is-open', open);
    this.mobileNav.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('mnav-open', open);
    open ? ScrollLock.lock() : ScrollLock.unlock();
    $$('[data-menu-toggle]').forEach(btn => btn.setAttribute('aria-expanded', String(open)));
    if (open) {
      this.mnavReset();
      // focus the close button for keyboard users
      const closeBtn = this.mobileNav.querySelector('#mobile-nav-close');
      if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
    }
  },

  closeMobileNav() {
    if (!this.mobileNav) return;
    const wasOpen = this.mobileNav.classList.contains('is-open');
    this.mobileNav.classList.remove('is-open');
    if (this.mobileOverlay) this.mobileOverlay.classList.remove('is-open');
    this.mobileNav.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mnav-open');
    if (wasOpen) ScrollLock.unlock();
    $$('[data-menu-toggle]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
    // return focus to the hamburger that opened the drawer
    const trigger = document.querySelector('[data-menu-toggle]');
    if (wasOpen && trigger) trigger.focus();
    // reset panel state after the close animation
    setTimeout(() => this.mnavReset(), 350);
  },

  /* ─── Drill-down panel stack ───────────────────────────── */
  mnavReset() {
    if (!this.mobileNav) return;
    this.mnavStack = ['root'];
    $$('.mobile-nav__panel', this.mobileNav).forEach(panel => {
      const isRoot = panel.dataset.mnavPanel === 'root';
      panel.classList.toggle('is-active', isRoot);
      panel.classList.remove('is-prev');
      panel.setAttribute('aria-hidden', isRoot ? 'false' : 'true');
    });
    $$('[data-mnav-drill]', this.mobileNav).forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  },

  mnavGoTo(panelId) {
    if (!this.mobileNav) return;
    const target = this.mobileNav.querySelector(`[data-mnav-panel="${panelId}"]`);
    if (!target) return;
    const current = this.mnavStack[this.mnavStack.length - 1];
    if (current === panelId) return;

    const currentEl = this.mobileNav.querySelector(`[data-mnav-panel="${current}"]`);
    if (currentEl) {
      currentEl.classList.remove('is-active');
      currentEl.classList.add('is-prev');
      currentEl.setAttribute('aria-hidden', 'true');
    }
    target.classList.remove('is-prev');
    target.classList.add('is-active');
    target.setAttribute('aria-hidden', 'false');
    // scroll the new panel to the top
    target.scrollTop = 0;
    // focus the back button for keyboard users
    const backBtn = target.querySelector('[data-mnav-back]');
    if (backBtn) setTimeout(() => backBtn.focus(), 50);
    // mark the drill trigger as expanded
    const drillBtn = this.mobileNav.querySelector(`[data-mnav-drill="${panelId}"]`);
    if (drillBtn) drillBtn.setAttribute('aria-expanded', 'true');
    this.mnavStack.push(panelId);
  },

  mnavBack() {
    if (!this.mobileNav || this.mnavStack.length <= 1) return;
    const current = this.mnavStack.pop();
    const prev = this.mnavStack[this.mnavStack.length - 1];
    const currentEl = this.mobileNav.querySelector(`[data-mnav-panel="${current}"]`);
    const prevEl = this.mobileNav.querySelector(`[data-mnav-panel="${prev}"]`);
    if (currentEl) {
      currentEl.classList.remove('is-active');
      currentEl.setAttribute('aria-hidden', 'true');
    }
    if (prevEl) {
      prevEl.classList.remove('is-prev');
      prevEl.classList.add('is-active');
      prevEl.setAttribute('aria-hidden', 'false');
      // return focus to the drill button that opened the panel we just closed
      const drillBtn = prevEl.querySelector(`[data-mnav-drill="${current}"]`);
      if (drillBtn) {
        drillBtn.setAttribute('aria-expanded', 'false');
        setTimeout(() => drillBtn.focus(), 50);
      }
    }
  },

  initMobilePanels() {
    if (!this.mobileNav) return;
    this.mnavStack = ['root'];

    // close buttons (root + every sub-panel)
    $$('.mobile-nav__close, #mobile-nav-close', this.mobileNav).forEach(btn =>
      on(btn, 'click', () => this.closeMobileNav())
    );

    // drill-down triggers
    $$('[data-mnav-drill]', this.mobileNav).forEach(btn =>
      on(btn, 'click', (e) => {
        e.preventDefault();
        this.mnavGoTo(btn.getAttribute('data-mnav-drill'));
      })
    );

    // back triggers
    $$('[data-mnav-back]', this.mobileNav).forEach(btn =>
      on(btn, 'click', (e) => {
        e.preventDefault();
        this.mnavBack();
      })
    );

    // ESC closes drawer; left-arrow drills back
    on(document, 'keydown', (e) => {
      if (!this.mobileNav.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.closeMobileNav(); }
      if (e.key === 'ArrowLeft' && this.mnavStack.length > 1) { e.preventDefault(); this.mnavBack(); }
    });

    // swipe-right to go back (mobile gesture)
    let touchStartX = 0, touchStartY = 0, touchTracking = false;
    on(this.mobileNav, 'touchstart', (e) => {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchTracking = this.mnavStack.length > 1;
    }, { passive: true });
    on(this.mobileNav, 'touchend', (e) => {
      if (!touchTracking) return;
      const dx = (e.changedTouches[0].clientX - touchStartX);
      const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
      if (dx > 60 && dy < 40) this.mnavBack();
      touchTracking = false;
    }, { passive: true });
  }
};

/* ============================================================
   SLIDESHOW
   ============================================================ */

class Slideshow {
  constructor(el) {
    this.el = el;
    this.slides = $$('.slideshow__slide', el);
    this.dots   = $$('.slideshow__dot',   el);
    this.current    = 0;
    this.total      = this.slides.length;
    this.autoplay   = el.dataset.autoplay === 'true';
    this.speed      = (parseInt(el.dataset.speed)    || 5)   * 1000;
    this.duration   =  parseInt(el.dataset.duration) || 700;
    this.transition = el.dataset.transition || 'fade';
    this.busy  = false;
    this.timer = null;

    if (this.total < 2) return;
    this.bindEvents();
    if (this.autoplay) this.startAutoplay();
  }

  bindEvents() {
    const prev = this.el.querySelector('.slideshow__arrow--prev');
    const next = this.el.querySelector('.slideshow__arrow--next');
    // Reset the autoplay timer on every manual nav so the user gets the
    // *full* configured duration to read the slide they just selected.
    if (prev) on(prev, 'click', () => { this.prev(); this.resetAutoplay(); });
    if (next) on(next, 'click', () => { this.next(); this.resetAutoplay(); });
    this.dots.forEach((dot, i) => on(dot, 'click', () => { this.goTo(i); this.resetAutoplay(); }));

    // Touch / swipe
    let startX = 0;
    on(this.el, 'touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    on(this.el, 'touchend',   e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.next() : this.prev();
        this.resetAutoplay();
      }
    });
  }

  /* Per-slide duration override: each .slideshow__slide can carry
     data-stay="<seconds>" (set from the block's "Stay on this slide for"
     range). When > 0 it wins over the section's default `speed`. */
  getSlideDuration(index) {
    const slide = this.slides[index];
    if (slide && slide.dataset.stay) {
      const stay = parseFloat(slide.dataset.stay);
      if (stay > 0) return stay * 1000;
    }
    return this.speed;
  }

  goTo(index, dir) {
    if (this.busy) return;
    const prevIdx = this.current;
    this.current  = (index + this.total) % this.total;
    if (prevIdx === this.current) return;

    const prevSlide = this.slides[prevIdx];
    const nextSlide = this.slides[this.current];
    const forward   = dir !== undefined ? dir >= 0 : this.current > prevIdx;
    const t   = this.transition;
    const dur = this.duration;

    this.busy = true;
    setTimeout(() => { this.busy = false; }, dur + 250);

    // Dots
    if (this.dots[prevIdx])    this.dots[prevIdx].classList.remove('is-active');
    if (this.dots[this.current]) this.dots[this.current].classList.add('is-active');

    // Clear leftover leaving state
    this.slides.forEach(s => s.classList.remove('is-leaving'));

    if (t === 'slide') {
      // JS-driven: slides are position:absolute so track transform does nothing.
      // Manually set start position of incoming, then animate both.
      const startPos = forward ? '100%' : '-100%';
      const exitPos  = forward ? '-100%' : '100%';

      nextSlide.style.cssText += ';transition:none;transform:translateX(' + startPos + ');opacity:1;z-index:2';
      prevSlide.style.cssText += ';opacity:1';
      nextSlide.offsetHeight; // force reflow

      const ease = `transform ${dur}ms ease`;
      prevSlide.style.transition = ease;
      nextSlide.style.transition = ease;

      prevSlide.classList.remove('is-active');
      nextSlide.classList.add('is-active');
      prevSlide.style.transform = `translateX(${exitPos})`;
      nextSlide.style.transform = 'translateX(0)';

      setTimeout(() => {
        [prevSlide, nextSlide].forEach(s => {
          s.style.transform = '';
          s.style.opacity   = '';
          s.style.transition = '';
          s.style.zIndex    = '';
        });
      }, dur + 60);

    } else {
      // CSS-driven: class changes trigger CSS transitions / animations.
      // Remove is-active, force reflow to restart @keyframe, re-add.
      prevSlide.classList.remove('is-active');
      prevSlide.classList.add('is-leaving');
      nextSlide.classList.remove('is-active'); // ensure animation restarts
      nextSlide.offsetHeight;                  // flush style / restart animation
      nextSlide.classList.add('is-active');

      setTimeout(() => prevSlide.classList.remove('is-leaving'), dur + 120);
    }
  }

  next() { this.goTo(this.current + 1,  1); }
  prev() { this.goTo(this.current - 1, -1); }

  startAutoplay() {
    this.scheduleNext();
    on(this.el, 'mouseenter', () => this.cancelAutoplay());
    on(this.el, 'mouseleave', () => this.scheduleNext());
  }

  /* Recursive setTimeout — each call re-reads the *current* slide's duration
     so a per-slide override takes effect as soon as we land on it. After the
     timeout fires `next()`, the recursive call uses the NEW current slide's
     duration. Safer than setInterval here: a fixed interval can't model
     per-slide cadence. */
  scheduleNext() {
    this.cancelAutoplay();
    if (!this.autoplay) return;
    const dur = this.getSlideDuration(this.current);
    this.timer = setTimeout(() => {
      this.next();
      this.scheduleNext();
    }, dur);
  }

  cancelAutoplay() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplay) this.scheduleNext();
  }
}

/* ============================================================
   TABS
   ============================================================ */

function initTabs() {
  $$('[data-tabs]').forEach(tabsEl => {
    const tabs = $$('.tabs__tab', tabsEl);
    const panels = $$('.tabs__panel', tabsEl);

    tabs.forEach((tab, i) => {
      on(tab, 'click', () => {
        tabs.forEach(t => { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
        panels.forEach(p => p.classList.remove('is-active'));
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
        if (panels[i]) panels[i].classList.add('is-active');
      });
    });
  });
}

/* ============================================================
   ACCORDION (FAQ)
   ============================================================ */

function initAccordions() {
  $$('[data-accordion]').forEach(acc => {
    const items = $$('.faq__item', acc);
    items.forEach(item => {
      const trigger = item.querySelector('.faq__question');
      const answer = item.querySelector('.faq__answer');
      if (!trigger || !answer) return;

      on(trigger, 'click', () => {
        const open = item.classList.contains('is-open');
        // Close all
        items.forEach(i => {
          i.classList.remove('is-open');
          const a = i.querySelector('.faq__answer');
          if (a) a.style.maxHeight = null;
        });
        // Open clicked
        if (!open) {
          item.classList.add('is-open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });
  });
}

/* ============================================================
   COUNTDOWN TIMER
   ============================================================ */

function initCountdowns() {
  $$('[data-countdown]').forEach(el => {
    const end = new Date(el.dataset.countdown).getTime();
    if (isNaN(end)) return;

    const days = el.querySelector('[data-days]');
    const hours = el.querySelector('[data-hours]');
    const mins = el.querySelector('[data-mins]');
    const secs = el.querySelector('[data-secs]');

    function update() {
      const now = Date.now();
      const diff = end - now;

      if (diff <= 0) {
        el.textContent = el.dataset.expiredMessage || 'Event has ended';
        return;
      }

      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      if (days) days.textContent = String(d).padStart(2, '0');
      if (hours) hours.textContent = String(h).padStart(2, '0');
      if (mins) mins.textContent = String(m).padStart(2, '0');
      if (secs) secs.textContent = String(s).padStart(2, '0');
    }

    update();
    setInterval(update, 1000);
  });
}

/* ============================================================
   BEFORE / AFTER SLIDER
   ============================================================ */

function initBeforeAfter() {
  $$('.before-after').forEach(el => {
    const handle = el.querySelector('.before-after__handle');
    const before = el.querySelector('.before-after__before');
    const startPos = parseFloat(el.dataset.handlePosition) || 50;

    function setPosition(pct) {
      pct = Math.max(5, Math.min(95, pct));
      if (handle) handle.style.left = `${pct}%`;
      if (before) before.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    }

    setPosition(startPos);

    let dragging = false;

    function getX(e) {
      return e.touches ? e.touches[0].clientX : e.clientX;
    }

    function onMove(e) {
      if (!dragging) return;
      const rect = el.getBoundingClientRect();
      const pct = ((getX(e) - rect.left) / rect.width) * 100;
      setPosition(pct);
    }

    on(handle, 'mousedown', () => dragging = true);
    on(handle, 'touchstart', () => dragging = true, { passive: true });
    on(window, 'mousemove', onMove);
    on(window, 'touchmove', onMove, { passive: true });
    on(window, 'mouseup', () => dragging = false);
    on(window, 'touchend', () => dragging = false);
  });
}

/* ============================================================
   SCROLL ANIMATIONS
   ============================================================ */

function initScrollAnimations() {
  if (!window.IntersectionObserver) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

  $$('[data-animate]').forEach(el => observer.observe(el));
}

/* ============================================================
   STAT COUNTER ANIMATION
   ============================================================ */

function initStatCounters() {
  if (!window.IntersectionObserver) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const target = parseFloat(el.dataset.count);
      const duration = 2000;
      const start = performance.now();
      const isDecimal = String(target).includes('.');

      function step(now) {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = target * eased;
        el.textContent = isDecimal ? value.toFixed(1) : Math.round(value).toLocaleString();
        if (progress < 1) requestAnimationFrame(step);
      }

      requestAnimationFrame(step);
      observer.unobserve(el);
    });
  }, { threshold: 0.5 });

  $$('[data-count]').forEach(el => observer.observe(el));
}

/* ============================================================
   BACK TO TOP
   ============================================================ */

function initBackToTop() {
  const btn = $('#back-to-top');
  if (!btn) return;

  on(window, 'scroll', debounce(() => {
    btn.classList.toggle('is-visible', window.scrollY > 400);
  }, 100), { passive: true });

  on(btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
}

/* ============================================================
   COOKIE BANNER
   ============================================================ */

function initCookieBanner() {
  const banner = $('#cookie-banner');
  if (!banner) return;
  if (localStorage.getItem('prism-cookies-accepted')) return;

  banner.style.display = '';

  const accept = $('#cookie-accept');
  const decline = $('#cookie-decline');

  on(accept, 'click', () => {
    localStorage.setItem('prism-cookies-accepted', '1');
    banner.style.display = 'none';
  });

  on(decline, 'click', () => {
    localStorage.setItem('prism-cookies-accepted', '0');
    banner.style.display = 'none';
  });
}

/* ============================================================
   ANNOUNCEMENT BAR AUTO-ROTATE
   ============================================================ */

function initAnnouncementSlider() {
  $$('[data-announcement-slider]').forEach(slider => {
    const items = $$('.announcement-bar__item', slider);
    if (items.length < 2) return;
    let current = 0;

    setInterval(() => {
      items[current].classList.remove('is-active');
      current = (current + 1) % items.length;
      items[current].classList.add('is-active');
    }, 4000);
  });
}

/* ============================================================
   QUANTITY INPUTS (product page)
   ============================================================ */

function initQtyInputs() {
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-qty-action]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty-wrap]');
    if (!wrap) return;
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    const val = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 999;
    if (btn.dataset.qtyAction === 'minus') input.value = Math.max(min, val - 1);
    if (btn.dataset.qtyAction === 'plus') input.value = Math.min(max, val + 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/* ============================================================
   PRODUCT FORM — add to cart via AJAX
   ============================================================ */

function initProductForm() {
  $$('[data-product-form]').forEach(form => {
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[data-add-to-cart]');
      const idInput = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');
      if (!idInput) return;

      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

      try {
        const result = await Cart.add(idInput.value, qtyInput ? parseInt(qtyInput.value) : 1);
        // Cart.add throws on non-ok responses; if we get here it succeeded.
        await CartDrawer.refreshSection();
        CartDrawer.open();
        if (btn) {
          btn.textContent = 'Added!';
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = btn.dataset.label || 'Add to Cart';
          }, 1200);
        }
      } catch (err) {
        const msg = err && (err.description || err.message);
        if (btn) {
          btn.disabled = false;
          btn.textContent = msg || 'Error — try again';
          if (msg) setTimeout(() => { btn.textContent = btn.dataset.label || 'Add to Cart'; }, 3000);
        }
        console.error('Add to cart error:', err);
      }
    });
  });
}

/* ============================================================
   PAGE TRANSITIONS
   ============================================================ */

function initPageTransitions() {
  if (!document.body.dataset.transition) return;
  document.body.classList.add('page-transition-ready');

  on(document, 'click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (
      e.metaKey || e.ctrlKey || e.shiftKey ||
      link.target === '_blank' ||
      href.startsWith('#') ||
      href.startsWith('mailto') ||
      href.startsWith('tel')
    ) return;

    e.preventDefault();
    document.body.classList.add('page-exit');
    setTimeout(() => { window.location = href; }, 300);
  });

  window.addEventListener('pageshow', () => {
    document.body.classList.remove('page-exit');
  });
}

/* ============================================================
   VARIANT SELECTOR
   ============================================================ */

function initVariantSelector() {
  $$('[data-variant-select]').forEach(select => {
    on(select, 'change', () => {
      const form = select.closest('form');
      if (!form) return;
      // Update price, availability, image etc. based on selected variant
      const selectedOption = select.options[select.selectedIndex];
      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      const available = selectedOption.dataset.available === 'true';

      const priceEl = form.querySelector('[data-product-price]');
      const comparePriceEl = form.querySelector('[data-product-compare-price]');
      const addBtn = form.querySelector('[data-add-to-cart]');

      if (priceEl && price) priceEl.textContent = price;
      if (comparePriceEl && comparePrice) {
        comparePriceEl.textContent = comparePrice;
        comparePriceEl.hidden = !comparePrice;
      }
      if (addBtn) {
        addBtn.disabled = !available;
        addBtn.textContent = available ? (addBtn.dataset.label || 'Add to Cart') : 'Sold Out';
      }
    });
  });
}

/* ============================================================
   FILTER TABS (collection page)
   ============================================================ */

function initFilterTabs() {
  $$('[data-filter-tabs]').forEach(container => {
    const tabs = $$('[data-filter-tab]', container);
    tabs.forEach(tab => {
      on(tab, 'click', () => {
        tabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        // Could be extended to actually filter via URL params or AJAX
      });
    });
  });
}

/* ============================================================
   INIT
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  // In the theme editor the cart drawer should remain docked/closed.
  // Only attach open-on-click listeners when not in design mode.
  if (!window.Shopify?.designMode) {
    CartDrawer.init();
  } else {
    // Still wire up close triggers so editors can dismiss if it ever
    // becomes visible during section inspection.
    CartDrawer.drawer = document.getElementById('cart-drawer');
    document.querySelectorAll('[data-close-cart]').forEach(btn =>
      btn.addEventListener('click', () => CartDrawer.close())
    );
  }
  SearchDrawer.init();
  Header.init();
  initTabs();
  initAccordions();
  initCountdowns();
  initBeforeAfter();
  initScrollAnimations();
  initStatCounters();
  initBackToTop();
  initCookieBanner();
  initAnnouncementSlider();
  initQtyInputs();
  initProductForm();
  initPageTransitions();
  initVariantSelector();
  initFilterTabs();
  initParallax();
  initProductCardSwatches();

  // Slideshows
  $$('.slideshow').forEach(el => new Slideshow(el));
});

/* ============================================================
   PRODUCT CARD — colour swatches swap the thumbnail
   ============================================================ */
function initProductCardSwatches () {
  function ensureDefault (img) {
    if (img.dataset.defaultSrc == null) {
      img.dataset.defaultSrc    = img.getAttribute('src')    || '';
      img.dataset.defaultSrcset = img.getAttribute('srcset') || '';
      img.dataset.defaultAlt    = img.getAttribute('alt')    || '';
    }
  }

  function applyImg (img, src, srcset, alt) {
    if (!src) return;
    img.setAttribute('src', src);
    if (srcset != null) img.setAttribute('srcset', srcset);
    if (alt    != null) img.setAttribute('alt',    alt);
  }

  function applySwatch (img, sw) {
    applyImg(img, sw.dataset.img, sw.dataset.imgSrcset, sw.dataset.imgAlt);
  }

  function restoreCard (card, img) {
    // Sticky (clicked) wins over the original
    const active = card.querySelector('.product-card__swatch.is-active[data-img]');
    if (active) {
      applySwatch(img, active);
    } else {
      applyImg(img, img.dataset.defaultSrc, img.dataset.defaultSrcset, img.dataset.defaultAlt);
    }
  }

  // mouseover bubbles (mouseenter does not) — easier delegation
  document.addEventListener('mouseover', e => {
    const sw = e.target.closest && e.target.closest('.product-card__swatch[data-img]');
    if (!sw) return;
    const card = sw.closest('.product-card');
    const img  = card && card.querySelector('.product-card__img--primary');
    if (!img) return;
    ensureDefault(img);
    card.classList.add('is-swatch-hovered');
    applySwatch(img, sw);
  });

  document.addEventListener('mouseout', e => {
    const sw = e.target.closest && e.target.closest('.product-card__swatch');
    if (!sw) return;
    // Bubbling-up mouseout from a child element of the swatch — ignore
    if (e.relatedTarget && sw.contains(e.relatedTarget)) return;
    const card = sw.closest('.product-card');
    const img  = card && card.querySelector('.product-card__img--primary');
    if (!img) return;
    card.classList.remove('is-swatch-hovered');
    restoreCard(card, img);
  });

  // Click → sticky swap (also fires on touch / keyboard)
  document.addEventListener('click', e => {
    const sw = e.target.closest && e.target.closest('.product-card__swatch[data-img]');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.product-card');
    if (!card) return;
    card.querySelectorAll('.product-card__swatch.is-active').forEach(s => s.classList.remove('is-active'));
    sw.classList.add('is-active');
    const img = card.querySelector('.product-card__img--primary');
    if (!img) return;
    ensureDefault(img);
    applySwatch(img, sw);

    // Bonus: update the card's media link so clicking the thumbnail goes to the chosen variant
    if (sw.dataset.variantId) {
      const mediaLink = card.querySelector('.product-card__media-link');
      const titleLink = card.querySelector('.product-card__title a');
      [mediaLink, titleLink].forEach(a => {
        if (!a) return;
        const base = (a.getAttribute('href') || '').split('?')[0];
        a.setAttribute('href', base + '?variant=' + sw.dataset.variantId);
      });
    }
  });
}

/* ============================================================
   NEWSLETTER FORMS — Mailchimp / custom success state
   ============================================================ */

(function () {
  // For non-Shopify forms: show success message after submit
  // Mailchimp posts to _blank so the form page doesn't change —
  // we just show the success message immediately on submit.
  document.querySelectorAll('.nf-form[target="_blank"], .nf-form:not([id*="shopify"])').forEach(form => {
    form.addEventListener('submit', function () {
      const successEl = document.getElementById(form.id + '-success');
      if (successEl) {
        setTimeout(() => {
          form.style.display = 'none';
          successEl.classList.remove('nf-success--hidden');
        }, 300);
      }
    });
  });
})();

/* ============================================================
   PARALLAX BACKGROUNDS
   ============================================================ */

function initParallax() {
  const els = $$('[data-parallax]');
  if (!els.length) return;

  // Respect prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function updateParallax() {
    els.forEach(el => {
      const section = el.closest('.cb-section') || el.parentElement;
      const rect = section.getBoundingClientRect();
      // Only process when section is in / near the viewport
      if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
      const speed = parseFloat(el.dataset.parallax) || 0.3;
      // centre of section relative to centre of viewport
      const offset = (rect.top + rect.height / 2) - window.innerHeight / 2;
      el.style.transform = 'translateY(' + (offset * speed) + 'px)';
    });
  }

  updateParallax();

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => { updateParallax(); ticking = false; });
      ticking = true;
    }
  }, { passive: true });

  window.addEventListener('resize', debounce(updateParallax, 100));
}

/* ============================================================
   SHOPIFY SECTION EVENTS (live preview in editor)
   ============================================================ */

document.addEventListener('shopify:section:load', () => {
  initTabs();
  initAccordions();
  initCountdowns();
  initBeforeAfter();
  initScrollAnimations();
  initStatCounters();
  initAnnouncementSlider();
  initParallax();
  $$('.slideshow').forEach(el => { if (!el._sliderInit) { new Slideshow(el); el._sliderInit = true; } });
});

document.addEventListener('shopify:block:select', (e) => {
  const block = e.detail.blockId;
  // Auto-scroll to block in editor
});
